/* eslint-disable react-hooks/exhaustive-deps */
/*
 * IP Bans sub-tab — issue an IP ban (looks up the player&apos;s IP via RCON
 * `getip`, then bans both the player ID and the IP), or ban an IP
 * directly. Also exposes a "lookup IP" tool for diagnostics.
 */
import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Globe, Search } from "lucide-react";

export default function IpBansTab({ toast }) {
  const [servers, setServers] = useState([]);
  const [issue, setIssue] = useState({ player_id: "", reason: "", server: "" });
  const [direct, setDirect] = useState({ ip_address: "", reason: "", server: "" });
  const [lookup, setLookup] = useState({ player_id: "", server: "" });
  const [lookupResult, setLookupResult] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/admin/moderation/servers").then(({ data }) => setServers(data.servers || []));
  }, []);

  const doIssue = async (e) => {
    e.preventDefault();
    if (!issue.player_id || !issue.server) { toast.error("Player ID and server are required"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post("/admin/moderation/ip-bans", issue);
      toast.success(`IP-banned ${issue.player_id}${data.ip ? ` (${data.ip})` : " (IP unknown)"}`);
      setIssue({ player_id: "", reason: "", server: "" });
    } catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };

  const doDirect = async (e) => {
    e.preventDefault();
    if (!direct.ip_address || !direct.server) { toast.error("IP and server are required"); return; }
    setSubmitting(true);
    try {
      await api.post("/admin/moderation/ip-bans/direct", direct);
      toast.success(`IP ${direct.ip_address} banned`);
      setDirect({ ip_address: "", reason: "", server: "" });
    } catch (e) { toast.error(apiError(e)); } finally { setSubmitting(false); }
  };

  const doLookup = async (e) => {
    e.preventDefault();
    if (!lookup.player_id || !lookup.server) { toast.error("Player ID and server are required"); return; }
    setLookupResult(null);
    try {
      const { data } = await api.get(`/admin/moderation/ip-bans/lookup/${encodeURIComponent(lookup.player_id)}`, {
        params: { server: lookup.server },
      });
      setLookupResult(data);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-4">
      <form onSubmit={doIssue} className="glass-card p-4 space-y-3" data-testid="ipban-issue-form">
        <h3 className="label flex items-center gap-2"><Globe size={14} /> IP-ban via player lookup</h3>
        <input value={issue.player_id} onChange={(e) => setIssue({ ...issue, player_id: e.target.value })} placeholder="Player ID" className="input" data-testid="ipban-player-id" />
        <select value={issue.server} onChange={(e) => setIssue({ ...issue, server: e.target.value })} className="input" data-testid="ipban-server">
          <option value="">Pick server (RCON used to resolve IP)</option>
          {servers.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
        </select>
        <input value={issue.reason} onChange={(e) => setIssue({ ...issue, reason: e.target.value })} placeholder="Reason" className="input" data-testid="ipban-reason" />
        <button disabled={submitting} className="btn btn-rose">Issue IP Ban</button>
      </form>

      <form onSubmit={doDirect} className="glass-card p-4 space-y-3" data-testid="ipban-direct-form">
        <h3 className="label flex items-center gap-2"><Globe size={14} /> Ban an IP directly</h3>
        <input value={direct.ip_address} onChange={(e) => setDirect({ ...direct, ip_address: e.target.value })} placeholder="IP address (e.g. 1.2.3.4)" className="input" data-testid="ipban-direct-ip" />
        <select value={direct.server} onChange={(e) => setDirect({ ...direct, server: e.target.value })} className="input" data-testid="ipban-direct-server">
          <option value="">Server</option>
          {servers.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
        </select>
        <input value={direct.reason} onChange={(e) => setDirect({ ...direct, reason: e.target.value })} placeholder="Reason" className="input" data-testid="ipban-direct-reason" />
        <button disabled={submitting} className="btn btn-rose">Ban IP</button>
      </form>

      <form onSubmit={doLookup} className="glass-card p-4 space-y-3 lg:col-span-2" data-testid="ipban-lookup-form">
        <h3 className="label flex items-center gap-2"><Search size={14} /> Look up a player&apos;s IP</h3>
        <div className="grid sm:grid-cols-3 gap-3">
          <input value={lookup.player_id} onChange={(e) => setLookup({ ...lookup, player_id: e.target.value })} placeholder="Player ID" className="input sm:col-span-1" data-testid="ipban-lookup-player" />
          <select value={lookup.server} onChange={(e) => setLookup({ ...lookup, server: e.target.value })} className="input sm:col-span-1" data-testid="ipban-lookup-server">
            <option value="">Server</option>
            {servers.map((s) => <option key={s.name} value={s.name}>{s.name}</option>)}
          </select>
          <button className="btn btn-ghost sm:col-span-1">Look up</button>
        </div>
        {lookupResult && (
          <div className="text-sm border-l-2 border-[var(--teal)] pl-3" data-testid="ipban-lookup-result">
            <div>IP: <span className="font-mono text-[var(--teal)]">{lookupResult.ip || "(not found)"}</span></div>
            {lookupResult.raw ? <pre className="text-xs text-[var(--muted)] mt-1 whitespace-pre-wrap">{lookupResult.raw}</pre> : null}
          </div>
        )}
      </form>
    </div>
  );
}
